import { create } from 'zustand'
import { BIRD_NAME } from '../lib/world/identity'
import { foldDisk } from '../lib/world/diskScreen'
import { mergeEntities } from '../lib/world/entityMerge'

// What to call it when he answers a walker's mark with one of his own. Written
// as things a small bird does, not as protocol.
const BIRD_REPLY_LINES = {
    note: `${BIRD_NAME} peeps back at you`,
    wave: `${BIRD_NAME} bobs his head at you`,
    orb: `${BIRD_NAME} holds up a little light`,
    question: `${BIRD_NAME} tilts his head at you`,
    apple: `${BIRD_NAME} shows you an apple`,
    whistle: `${BIRD_NAME} peeps back at you`,
}
import { persist, createJSONStorage } from 'zustand/middleware'

const MAX_EVENTS = 100 // Keep last 100 events

// persist() wraps the store so a small subset of state survives reloads
// via localStorage. partialize() controls which fields persist — keep
// this tight so we don't accidentally snapshot transient runtime state
// (events, NPC positions, etc) into localStorage where it'd grow unbounded.
// persist() used to serialise + write localStorage on EVERY set(), and
// world deltas fire 3 sets each at 5-10Hz: 15-30 synchronous writes a
// second to persist two booleans, felt as intermittent frame hitches
// while walking. The debounced storage below is the fix: partialize
// still runs per set (two booleans, negligible) but the write lands at
// most once a second. Only introSeen/uiVisible persist.
function debouncedLocalStorage() {
  let timer = null
  let pendingKey = null
  let pendingValue = null
  return {
    getItem: (k) => window.localStorage.getItem(k),
    setItem: (k, v) => {
      pendingKey = k
      pendingValue = v
      if (timer) return
      timer = setTimeout(() => {
        timer = null
        try { window.localStorage.setItem(pendingKey, pendingValue) } catch { /* quota/private mode */ }
      }, 1000)
    },
    removeItem: (k) => window.localStorage.removeItem(k),
  }
}

const useGameStore = create(persist((set, get) => ({
    // Server connection status (managed by ServerNPCBridge)
    connectionStatus: 'disconnected', // 'disconnected' | 'connecting' | 'connected'
    setConnectionStatus: (status) => set({ connectionStatus: status }),

    // Event feed - rolling log of NPC activities
    events: [],
    addEvent: (event) => set((state) => {
        const eventId = event.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`)
        // Deduplicate - skip if event with same ID already exists
        if (event.id && state.events.some(e => e.id === event.id)) {
            return state
        }
        return {
            events: [
                ...state.events.slice(-(MAX_EVENTS - 1)),
                {
                    ...event,
                    id: eventId,
                    timestamp: event.timestamp || Date.now(),
                }
            ]
        }
    }),

    // NPC states - current snapshot of all NPCs
    npcStates: [],
    setNpcStates: (npcs) => set({ npcStates: npcs }),

    // World entities (food spots, nests, shinies)
    entityStates: [],
    setEntityStates: (entities) => set({ entityStates: entities }),
    // The town's clock less this browser's, from the snapshot's serverTime:
    // what runs on the town's time (the monorail's lift) is drawn on it
    serverClockOffset: 0,
    setServerClockOffset: (ms) => set({ serverClockOffset: Number.isFinite(ms) ? ms : 0 }),
    // what changed since the town's last send, and what went away (lib/world/entityMerge.js)
    mergeEntityStates: (changed, gone) => set((s) => ({ entityStates: mergeEntities(s.entityStates, changed, gone) })),

    // Live environment state — updated from periodic world_delta broadcasts.
    // Kept separate from worldSnapshot so admin-facing live updates don't
    // trigger re-runs of effects that depend on the full snapshot reference
    // (notably ServerNPCBridge's slot-allocation snap).
    environment: null,
    setEnvironment: (env) => set({ environment: env }),

    // A compact read model of the stories, questions and commitments that
    // are still moving. LifeNow owns the poll; the journal reads the same
    // object so the first viewport and the deeper view cannot drift.
    lifeState: null,
    setLifeState: (life) => set({ lifeState: life }),

    // First-person interactions (transient, never persisted): what the
    // crosshair is over, the offering panel opened by clicking a crystal,
    // and the last wall-bump timestamp (drives the impact overlay).
    fpAim: null,
    setFpAim: (a) => set({ fpAim: a }),
    openedOffering: null,
    setOpenedOffering: (o) => set({ openedOffering: o }),
    wallBumpAt: 0,
    triggerWallBump: () => set({ wallBumpAt: Date.now() }),

    // Walker signals (whistle + marks). Local sends render optimistically and
    // the sim echo is deduped by id; marks fade after ~4s and get pruned.
    activeSignals: [],
    addSignal: (sig) => set((state) => {
        if (sig.id && state.activeSignals.some((s) => s.id === sig.id)) return state
        const now = Date.now()
        const kept = state.activeSignals.filter((s) => s.expiresAt > now).slice(-19)
        const next = { activeSignals: [...kept, { ...sig, expiresAt: now + 4200 }] }
        // When the signal came from HIM, say so in words. A glyph appearing
        // over his head is easy to miss and easy to misread; a line naming
        // what he just did is the whole point of the exchange, and it's the
        // moment a visitor realises he noticed them.
        if (sig.from) {
            const line = BIRD_REPLY_LINES[sig.kind] || `${BIRD_NAME} answers`
            next.birdReply = { text: line, at: now }
        }
        return next
    }),
    // Last thing he said back, for the little line near the crosshair.
    birdReply: null,
    pruneSignals: () => set((state) => {
        const now = Date.now()
        const kept = state.activeSignals.filter((s) => s.expiresAt > now)
        return kept.length === state.activeSignals.length ? state : { activeSignals: kept }
    }),
    // Registered by ServerNPCBridge (owns the WS) so FP input can send.
    signalSender: null,
    setSignalSender: (fn) => set({ signalSender: fn }),
    // Registered by FirstPersonController so the SignalBar buttons know where
    // the player is standing.
    playerPosGetter: null,
    setPlayerPosGetter: (fn) => set({ playerPosGetter: fn }),
    signalBarOpen: false,
    setSignalBarOpen: (v) => set({ signalBarOpen: !!v }),

    // Memory consolidation state - overrides pipeline HUD while active
    // { active, progress (0-1), durationMs, startedAt, npcId } or null
    memoryConsolidation: null,
    startMemoryConsolidation: (durationMs = 5000, npcId = null) => set({
        memoryConsolidation: {
            active: true,
            progress: 0,
            durationMs,
            startedAt: Date.now(),
            npcId,
        },
    }),
    setMemoryConsolidationProgress: (progress) => set((state) => ({
        memoryConsolidation: state.memoryConsolidation
            ? { ...state.memoryConsolidation, progress }
            : null,
    })),
    endMemoryConsolidation: () => set({ memoryConsolidation: null }),

    // Live internet browse state — driven by `browse_state` WS messages from
    // sim-server's runBrowseSession. Lets BrowsingHUD show the watcher what
    // the bird is reading right now (page, depth, latest reaction) instead
    // of waiting for the journal poll. Cleared on session end / failure.
    //
    // Shape (whatever the latest phase emitted):
    //   { phase, npcId, npcName, timestamp, ...phasePayload }
    // Phases: 'start' | 'loading' | 'page' | 'reacted' | 'following' |
    //         'searching' | 'failed' | 'end'
    browseState: null,
    setBrowseState: (state) => set({ browseState: state }),
    clearBrowseState: () => set({ browseState: null }),

    // The floppy disk he is running at the cafe (sim world/disks/Screen.js). The town sends each step once, with only
    // what is new, as `disk_state`; they are folded here (lib/world/diskScreen.js) and not in the component, so two
    // steps landing before a render are never one. components/ui/DiskTerminal.jsx owns when it closes.
    diskScreen: null,
    pushDiskState: (msg) => set((state) => ({ diskScreen: foldDisk(state.diskScreen, msg) })),
    setDiskScreen: (screen) => set({ diskScreen: screen }),

    // UI overlay visibility — controls all informational HUDs (stats, journal,
    // activity, pipeline, omens, cam dots). Default true on every device;
    // Tab toggles for a clean cinematic view on desktop.
    // Initial value resolved by UIOverlayController on mount; null until then.
    // Corner menu. The four HUD buttons live behind one hamburger so the
    // world isn't ringed with chrome; opening it fans them out. Panel open
    // state lives here too, because the menu opens them and the panels
    // themselves need to close them.
    menuOpen: false,
    setMenuOpen: (v) => set({ menuOpen: !!v }),
    toggleMenu: () => set((s) => ({ menuOpen: !s.menuOpen })),

    journalOpen: false,
    setJournalOpen: (v) => set({ journalOpen: !!v, ...(v ? { journalUnseen: false } : {}) }),
    // His day opens first: the diary is the thing, the stories are a way
    // back through it.
    journalTab: 'diary',
    setJournalTab: (v) => set({ journalTab: ['stories', 'diary', 'wants', 'visitors', 'about'].includes(v) ? v : 'diary' }),
    // New diary entries since you last looked.
    journalUnseen: false,
    setJournalUnseen: (v) => set({ journalUnseen: !!v }),

    // A jump target for the diary: the actions history sets it, the journal
    // scrolls to the entry carrying that traceRef and lights it briefly.
    journalFocus: null,
    setJournalFocus: (v) => set({ journalFocus: v }),

    shrineOpen: false,
    setShrineOpen: (v) => set({ shrineOpen: !!v, ...(v ? { shrineUnseen: false } : { shrineTalk: null }) }),
    // A thread to land on: the drawer opens on the talk tab with this
    // one first and unfolded. Set from a /talk/<id> page's "join in" and
    // from the since strip; cleared when the drawer closes.
    shrineTalk: null,
    setShrineTalk: (id) => set({ shrineTalk: id || null }),
    // Badge state, so the folded hamburger can still say 'something happened'.
    shrineUnseen: false,
    setShrineUnseen: (v) => set((st) => ({
      shrineUnseen: !!v,
      // One signal, two readers. The corner cluster and the offerings tab
      // both colour off `notices`, so this keeps them from disagreeing with
      // the older flag rather than adding a second source of truth.
      notices: { ...st.notices, offerings: !!v },
    })),

    // Which kinds of thing have moved since you last looked. Colour-coded
    // in lib/ui/notices.js; the corner button and the profile tabs read the
    // same object so they can never disagree about what is waiting.
    notices: { offerings: false, calendar: false, you: false, mentions: false },

    // The two halves of a dot. `noticeState` is what the server last said
    // moved; `seen` is the newest thing you have actually looked at. A dot
    // is simply one being ahead of the other, which is why it survives a
    // reload, a route change and a week away, none of which the old
    // catch-it-live version did.
    //
    // Stamped with the SERVER's timestamp rather than Date.now(), so a
    // client with a wrong clock cannot mark itself permanently up to date.
    noticeState: { lastJudgedAt: 0, lastBallotAt: 0, openBallots: 0 },
    setNoticeState: (v) => set((s) => {
      const next = { ...s.noticeState, ...v }
      const same = Object.keys(next).every((k) => next[k] === s.noticeState[k])
      return same ? s : { noticeState: next }
    }),
    seen: { offerings: 0, calendar: 0 },
    markSeen: (kind) => set((s) => {
      const at = kind === 'offerings' ? s.noticeState.lastJudgedAt : s.noticeState.lastBallotAt
      if (!at || s.seen[kind] >= at) return s
      return { seen: { ...s.seen, [kind]: at } }
    }),
    setNotice: (kind, on) => set((s) => (
      s.notices[kind] === !!on ? s : { notices: { ...s.notices, [kind]: !!on } }
    )),
    clearNotice: (kind) => set((s) => (
      s.notices[kind] ? { notices: { ...s.notices, [kind]: false } } : s
    )),

    // The rank we last showed you. Persisted, because a "your rank changed"
    // dot that resets on refresh tells you nothing: the whole point is that
    // it survives until you have actually been and looked.
    seenRank: null,
    setSeenRank: (v) => set({ seenRank: v || null }),

    calendarOpen: false,
    setCalendarOpen: (v) => set({ calendarOpen: !!v, ...(v ? {} : { calendarToVote: false }) }),
    // The film festival's drawer: what it is, its rules and terms, and entering a film (components/ui/FestivalDrawer.jsx),
    // opened at its posters (FestivalHud) or from What's on.
    festivalOpen: false,
    setFestivalOpen: (v) => set({ festivalOpen: !!v }),
    // The Pino cam is on (?cam=pino, components/three/PinoCam.jsx): the
    // page hides its buttons and panels and draws the stream overlay.
    // null off, else the view: 'eyes' or 'shoulder'.
    pinoCam: null,
    setPinoCam: (v) => set({ pinoCam: v || null }),
    // Whether this opening should land on the vote rather than on what's
    // on. Set by whoever opened it: the menu knows the calendar dot is
    // lit, and the dot only ever means "a ballot you have not seen". The
    // drawer used to open on What's on and stamp the notice read on the
    // way in, so the vote the ping was about sat one tab over with nothing
    // left to mention it.
    calendarToVote: false,
    setCalendarToVote: (v) => set({ calendarToVote: !!v }),

    // What you have made. Opens ON TOP of the calendar rather than
    // replacing it, because you are usually there to pick one for the
    // event you are halfway through describing.
    postersOpen: false,
    setPostersOpen: (v) => set({ postersOpen: !!v }),
    // The one you chose. Lives here rather than in the form that wanted it
    // so the shelf can be mounted once, at page level, and still answer
    // whoever opened it.
    pickedPoster: null,
    setPickedPoster: (p) => set({ pickedPoster: p }),
    // Set by whoever reads standing, so the menu can decide whether to
    // offer the shelf without fetching /api/role on its own.
    canPoster: false,
    setCanPoster: (v) => set({ canPoster: !!v }),

    // You, in his town: your standing, your offerings, what he said about you.
    profileOpen: false,
    setProfileOpen: (v) => set({ profileOpen: !!v }),
    // Where to go once connecting is done, set by the door that sent you
    // to the connect card ('shrine' for leave him something). Read and
    // cleared by the menu the moment you are signed in.
    connectThen: null,
    setConnectThen: (v) => set({ connectThen: v || null }),
    // Which tab the drawer is on. In the store rather than the drawer so
    // whoever opens it can say where to land: the strip that tells you
    // he mentioned you opens straight onto the mentions, not onto a rank
    // card you then have to click past.
    profileTab: 'you',
    setProfileTab: (t) => set({ profileTab: t || 'you' }),

    // Who you are here: { username, called } once /api/me has synced,
    // null signed out. Filled by the auth island's bridge, read by the
    // connect button, the drawers, and anything that captions a submission.
    identity: null,
    setIdentity: (v) => set({ identity: v || null }),

    // Auth loads as an ISLAND: privy is megabytes of wallet stack and must
    // not race three.js for the boot main thread. wanted flips on a short
    // timer (returning visitors get recognised soon after the world starts)
    // or the moment an auth-facing drawer opens; ready/signedIn mirror the
    // island once it's up; authApi carries its functions (logout, getToken)
    // for code that lives outside it; authSlots are the drawer's portal
    // targets the island renders the privy-hooked cards into.
    authWanted: false,
    setAuthWanted: (v) => set({ authWanted: !!v }),
    authReady: false,
    setAuthReady: (v) => set({ authReady: !!v }),
    authSignedIn: false,
    setAuthSignedIn: (v) => set({ authSignedIn: !!v }),
    authApi: null,
    setAuthApi: (api) => set({ authApi: api || null }),
    authSlots: {},
    setAuthSlot: (kind, el) => set((s) => (
      s.authSlots[kind] === el ? s : { authSlots: { ...s.authSlots, [kind]: el } }
    )),

    // Toasts — the one voice for every panel's "that worked" / "that
    // didn't". Slide in bottom-right, remove themselves. Panels push via
    // pushToast(text, good); the rack in Toasts.jsx does the rest.
    toasts: [],
    pushToast: (text, good = true) => set((s) => ({
        toasts: [...s.toasts.slice(-3), { id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, text: String(text), good: !!good, at: Date.now() }],
    })),
    dropToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    // Photo mode — hides every scrap of chrome and frames the view to an
    // Instagram crop so a visitor can take a real picture of him. Deliberately
    // not persisted: it's a moment, not a preference.
    photoMode: false,
    setPhotoMode: (v) => set({ photoMode: !!v }),
    togglePhotoMode: () => set((s) => ({ photoMode: !s.photoMode })),

    uiVisible: null,

  // The sky has snapped to the real time of day at least once (sky2.jsx
  // sets this on its first frame with a live dayPhase). The loading veil
  // waits for it, so the night arriving never happens in front of anyone.
  skySettled: false,
  setSkySettled: (v) => set({ skySettled: v }),

  // Every material in the town has its pipeline built (Precompiler's boot
  // pass). The veil waits for this too, so nobody turns round into a
  // half-second of shader compiles.
  worldCompiled: false,
  setWorldCompiled: (v) => set({ worldCompiled: v }),

  // Offering ids belonging to the signed-in visitor, fed by OfferingsDock's
  // poll. The minimap turns these into gold markers so people can go and
  // find what they left.
  myOfferingIds: [],
  setMyOfferingIds: (ids) => set({ myOfferingIds: Array.isArray(ids) ? ids : [] }),
    setUiVisible: (visible) => set({ uiVisible: !!visible }),
    toggleUiVisible: () => set((state) => ({ uiVisible: !state.uiVisible })),

    // Drone camera mode — entered by pressing D, exited by Esc.
    // While active, CameraController bails (lets DroneCamera drive),
    // auto-cycle pauses, and HUDs hide for cinematic flight feel.
    droneActive: false,
    setDroneActive: (active) => set({ droneActive: !!active }),

    // The close view of his vending machine (components/ui/VendingHud.jsx): the walker stands down and the camera
    // goes to the glass, the way it does for the drone.
    vendingOpen: false,
    setVendingOpen: (open) => set({ vendingOpen: !!open }),
    // and the job centre's (components/ui/JobCentreHud.jsx), the same way: across the street from its window
    jobsOpen: false,
    setJobsOpen: (open) => set({ jobsOpen: !!open }),
    // His bag's pop-up (components/ui/BagPanel.jsx), opened by a click on him (components/three/npc/BirdHit.jsx). The
    // walker keeps walking: it is a drawer over the world, not a view of its own.
    bagOpen: false,
    setBagOpen: (open) => set({ bagOpen: !!open }),

    // Live tunables mirror — sourced from the sim-server's admin_result
    // messages so the admin panel reflects ACTUAL server state instead
    // of optimistically guessing. Populated by useSimConnection's
    // admin_result handler. `null` until the first get_tunables echo.
    tunables: null,
    setTunables: (t) => set({ tunables: t }),
    // Patch a single field after a successful set_tunable. Keeps the
    // mirror coherent without re-fetching the whole object.
    patchTunable: (path, value) => set((state) => ({
        tunables: state.tunables ? { ...state.tunables, [path]: value } : state.tunables,
    })),

    // Last admin_result — handy for surfacing failures (e.g. rejected
    // tunable value) in the UI. Cleared on next command.
    lastAdminResult: null,
    setLastAdminResult: (r) => set({ lastAdminResult: r }),

    // ─── Persisted across reloads ───────────────────────────────
    // First-visit cinematic intro has shown + completed. Once true,
    // VictorIntro never re-renders. Returning visitors only ever see
    // the info modal (via the brain icon), not the cinematic.
    introSeen: false,
    setIntroSeen: (v) => set({ introSeen: !!v }),
}),
{
  name: 'kiwiexe.store',
  storage: createJSONStorage(() => (typeof window !== 'undefined' ? debouncedLocalStorage() : undefined)),
  // Only the bits that should survive a refresh. Everything else is
  // runtime-only — re-derived from the WS feed on connect.
  partialize: (state) => ({
    introSeen: state.introSeen,
    uiVisible: state.uiVisible,
    seenRank: state.seenRank,
    seen: state.seen,
  }),
  version: 1,
}))


// Dev-only console handle. Overlays like the browse popup are driven entirely
// by server events, so there was no way to look at one without waiting for him
// to happen to do the thing. In development:
//
//   __pino.getState().setBrowseState({ phase: 'page', url: '...', title: '...' })
//
// Stripped from production builds (NODE_ENV is inlined at compile).
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  window.__pino = useGameStore
}

export default useGameStore
